<template>
  <div class="p-6">
    <h2 class="mb-4 text-lg font-bold">{{ $t('W: Rapportsprog') }}</h2>

    <div class="mb-5">
      <UiLangRadioList
        v-model="selectedLocale"
        name="reportLocale"
        :options="displayLanguageOptions" />
    </div>

    <p
      v-if="errorText"
      class="mb-4 text-sm text-feedback-error">
      {{ errorText }}
    </p>

    <div class="flex justify-end gap-3">
      <UiBaseButton
        :text="$t('W: Annuller')"
        theme="neutral"
        @button-click="$emit('close')" />
      <UiBaseButton
        :text="$t('W: Gem')"
        :isLoading="isLoading"
        :showSaveIcon="showSaveIcon"
        :isDisabled="!selectedLocale || selectedLocale === reportLocale"
        @button-click="onSubmit" />
    </div>
  </div>
</template>

<script lang="ts">
import { useOrderStore } from '@/stores/orderStore'
import { useAppStore } from '@/stores/appStore'
import { useCustomerAccountStore } from '@/stores/customerAccountStore'
import { useInsuranceCustomerAccountStore } from '@/stores/insuranceCustomerAccountStore'
import { useCustomerUserStore } from '@/stores/customerUserStore'
import {
  LAB_CUSTOMER_LANGUAGES,
  LOCALE_COUNTRY_CODES,
  LOCALE_NATIVE_NAMES,
  type ILabLanguage,
  type TLabCustomerLanguageCode,
  type TLabCustomerLocale,
} from '@team-milva/lab-models/language'
import type { ILangListOption } from '@team-milva/lab-components'
import { mapActions, mapState } from 'pinia'
import { useToast } from '@/globals/typescript/utils/toast'

export default {
  name: 'UpdateOrderReportLocaleModal',

  emits: ['close'],

  props: {
    orderID: { type: String, required: true },
    reportLocale: { type: String, default: null },
  },

  data() {
    return {
      selectedLocale: (this.reportLocale ?? '') as string,
      isLoading: false,
      showSaveIcon: false,
      errorText: '',
    }
  },

  computed: {
    ...mapState(useAppStore, ['defaultMessageSaveIconTime']),
    ...mapState(useOrderStore, ['allOrders']),
    ...mapState(useCustomerAccountStore, ['allCustomerAccounts']),
    ...mapState(useInsuranceCustomerAccountStore, ['allInsuranceCustomerAccounts']),
    ...mapState(useCustomerUserStore, ['customerUsersPrivate']),

    order() {
      return this.allOrders.find((o) => o.id === this.orderID) ?? null
    },

    // The languages actually valid for this order's own customer — a business
    // or insurance account's own supportedLocales, or a private customer's single language.
    // Never the lab's full customer-language menu (that's a ceiling, not a
    // per-customer setting).
    validLanguages(): ILabLanguage[] {
      const order = this.order
      if (!order) return []

      if (order.segment === 'private') {
        const privateUser = this.customerUsersPrivate.find(
          (x) => x.id === order.privateUserID
        )
        const code = (privateUser?.languageCode ??
          'EN') as TLabCustomerLanguageCode
        const lang = LAB_CUSTOMER_LANGUAGES[code]
        return lang ? [lang] : []
      }

      // An insurance customer's own order uses the insurance customer's languages (MIL-402).
      const supportedLocales =
        (order.customer.type === 'insurance'
          ? this.allInsuranceCustomerAccounts.find((x) => x.id === order.customer.id)
              ?.supportedLocales
          : this.allCustomerAccounts.find((x) => x.id === order.customerAccountID)
              ?.supportedLocales) ?? []
      return Object.values(LAB_CUSTOMER_LANGUAGES).filter((lang) =>
        supportedLocales.includes(lang.locale)
      )
    },

    // The order's current reportLocale is always shown, even once it falls
    // outside its customer's valid language set — it is never force-reset,
    // only future selections are restricted to the valid options.
    displayLanguages(): ILabLanguage[] {
      const options: ILabLanguage[] = [...this.validLanguages]
      const hasCurrent = options.some(
        (lang) => lang.locale === this.reportLocale
      )
      if (this.reportLocale && !hasCurrent) {
        const stale = Object.values(LAB_CUSTOMER_LANGUAGES).find(
          (lang) => lang.locale === this.reportLocale
        )
        options.push(
          stale ?? {
            code: this.reportLocale,
            locale: this.reportLocale,
            name: this.reportLocale,
          }
        )
      }
      return options
    },

    displayLanguageOptions(): ILangListOption[] {
      return this.displayLanguages.map((lang) => ({
        value: lang.locale,
        text: LOCALE_NATIVE_NAMES[lang.locale as TLabCustomerLocale] ?? lang.name,
        countryCode: LOCALE_COUNTRY_CODES[lang.locale as TLabCustomerLocale] ?? lang.locale,
      }))
    },
  },

  methods: {
    ...mapActions(useOrderStore, ['updateOrderReportLocale']),

    async onSubmit(): Promise<void> {
      if (!this.selectedLocale) return
      this.isLoading = true
      this.errorText = ''
      try {
        await this.updateOrderReportLocale(this.orderID, this.selectedLocale)
        this.showSaveIcon = true
        setTimeout(() => {
          this.showSaveIcon = false
        }, this.defaultMessageSaveIconTime)
        useToast().success(this.$t('W: Rapportsprog opdateret'))
        this.$emit('close')
      } catch {
        this.errorText = this.$t('W: Der opstod en fejl')
        useToast().error(this.$t('W: Kunne ikke opdatere rapportsprog'))
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>
